import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SystemThemeProvider } from '../../hooks/useSystemTheme'
import { billingService } from '../../services/billingService';
import { paymentService } from '../../services/paymentService';
import BillModal from '../billing/BillModal';
import BillDetailsModal from '../billing/BillDetailsModal';
import EncodingStats from '../billing/EncodingStats';
import ConfirmationDialog from '../common/ConfirmationDialog';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Chip,
  InputAdornment,
  Pagination,
  Alert,
  CircularProgress,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  BarChart as BarChartIcon,
  Clear as ClearIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';

const BillingPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0); // 0: management, 1: stats
  const [bills, setBills] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [surchargeSettings, setSurchargeSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(10);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedBill, setSelectedBill] = useState(null);
  const [preSelectedCustomer, setPreSelectedCustomer] = useState(null);
  
  // Customer Search State
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  
  // Search Filters
  const [filterBarangay, setFilterBarangay] = useState('');
  const [filterType, setFilterType] = useState('Residential & Commercial');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [barangays, setBarangays] = useState([]);
  
  // Delete Confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  
  // Recent Bills Pagination
  const [currentRecentBillsPage, setCurrentRecentBillsPage] = useState(1);
  const recentBillsPerPage = 5;

  // Bill Details Modal
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [selectedBillDetails, setSelectedBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);

  const roundTo2 = (num) => Math.round(num * 100) / 100;

  const calculateBillsWithSurcharge = (bills, customer, settings = null) => {
    const settingsToUse = settings || surchargeSettings;
    if (!settingsToUse) return bills;
    
    // Sort bills by date (oldest first) to ensure credit applies correctly
    const sortedBills = [...bills].sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));
    
    let remainingCredit = customer ? parseFloat(customer.credit_balance || 0) : 0;
    
    return sortedBills.map(bill => {
      const surchargeInfo = paymentService.calculateSurcharge(bill, settingsToUse);
      
      const basicAmount = roundTo2(parseFloat(bill.basicamount || 0));
      const surchargeAmount = roundTo2(surchargeInfo ? surchargeInfo.surchargeAmount : parseFloat(bill.surchargeamount || 0));
      
      let calculatedDiscountAmount = 0;
      const consumption = parseFloat(bill.consumption || 0);

      // Discount logic: Deducted from basic amount if consumption > 1
      if (consumption > 1) {
        if (customer && customer.discount) {
          const customerDiscountPercent = parseFloat(customer.discount);
          calculatedDiscountAmount = roundTo2((basicAmount * customerDiscountPercent) / 100);
        } else if (bill.discountamount && parseFloat(bill.discountamount) > 0) {
          calculatedDiscountAmount = roundTo2(parseFloat(bill.discountamount));
        }
      }
      
      const basicAfterDiscount = roundTo2(Math.max(0, basicAmount - calculatedDiscountAmount));
      
      // Apply credit strictly to basic amount; if credit > basic, excess rolls over to next bill
      let creditApplied = 0;
      if (remainingCredit > 0) {
        creditApplied = roundTo2(Math.min(remainingCredit, basicAfterDiscount));
        remainingCredit = roundTo2(remainingCredit - creditApplied);
      }
      
      const netBasic = roundTo2(basicAfterDiscount - creditApplied);
      const totalBeforeCredit = roundTo2(basicAfterDiscount + surchargeAmount);
      const totalAfterCredit = roundTo2(netBasic + surchargeAmount);
      
      return {
        ...bill,
        surchargeInfo,
        calculatedDiscountAmount,
        calculatedTotal: totalBeforeCredit,
        netTotal: totalAfterCredit,
        creditApplied: creditApplied
      };
    });
  };

  const handleViewBillDetails = async (bill) => {
    if (!bill || !bill.customerid) return;

    setLoadingBillDetails(true);
    setShowBillDetails(true);
    
    try {
      const customerResult = await paymentService.getCustomerDetails(bill.customerid);
      const customerData = customerResult.success ? customerResult.data : null;

      const billResult = await paymentService.getUnpaidBills(bill.customerid);
      const historyResult = await paymentService.getPaymentHistory(bill.customerid);
      
      let billData = null;
      let isUnpaid = false;
      
      // Try to find in unpaid bills first
      if (billResult.success) {
        billData = billResult.data.find(b => b.billid === bill.billid);
        if (billData) isUnpaid = true;
      }
      
      // If not in unpaid, check history (paid bills)
      if (!billData && historyResult.success) {
        billData = historyResult.data.find(b => b.billid === bill.billid);
      }
      
      // Fallback to the bill object from the table if not found (though details might be sparse)
      if (!billData) {
         billData = bill;
      }

      if (billData && customerData) {
        // We also want to pass the full history to the modal for the graph
        const fullHistory = historyResult.success ? historyResult.data : [];
        
        let enrichedBill = billData;
        
        if (isUnpaid) {
          // For unpaid bills, calculate surcharge and credit allocation
          const billsWithSurcharge = calculateBillsWithSurcharge(billResult.data, customerData);
          enrichedBill = billsWithSurcharge.find(b => b.billid === bill.billid) || billData;
        } else {
          // For paid bills, use existing data
          enrichedBill = {
            ...billData,
            creditApplied: billData.advancepaymentamount || 0,
            calculatedTotal: billData.totalbillamount
          };
        }
        
        setSelectedBillDetails({
          bill: enrichedBill,
          customer: customerData,
          history: fullHistory
        });
      } else {
        setError('Unable to load bill details');
        setShowBillDetails(false);
      }
    } catch (err) {
      console.error('Error loading bill details:', err);
      setError('Error loading bill details');
      setShowBillDetails(false);
    } finally {
      setLoadingBillDetails(false);
    }
  };

  useEffect(() => {
    loadBills();
    loadCustomerTypes();
    loadSurchargeSettings();
    loadBarangays();

    const handleEditBillEvent = (event) => {
      const billToEdit = event.detail;
      handleEdit(billToEdit);
    };

    const handleDeleteBillEvent = (event) => {
      const billData = event.detail;
      const bill = typeof billData === 'object' ? billData : bills.find(b => b.billid === billData);
      if (bill) {
        handleDeleteClick(bill);
      }
    };

    window.addEventListener('editBill', handleEditBillEvent);
    window.addEventListener('deleteBill', handleDeleteBillEvent);

    return () => {
      window.removeEventListener('editBill', handleEditBillEvent);
      window.removeEventListener('deleteBill', handleDeleteBillEvent);
    };
  }, []);

  useEffect(() => {
    if (!location.search) return;

    const params = new URLSearchParams(location.search);
    const customerId = params.get('customerid');

    if (!customerId) return;

    const openAddForCustomerFromQuery = async () => {
      try {
        const result = await paymentService.getCustomerDetails(customerId);
        if (!result.success || !result.data) {
          return;
        }
        setActiveTab(0);
        setPreSelectedCustomer(result.data);
        setModalMode('add');
        setShowModal(true);
      } catch (e) {
        console.error('Error loading customer from query params:', e);
      }
    };

    openAddForCustomerFromQuery();
  }, [location.search]);

  useEffect(() => {
    const hasSearchTerm = customerSearch.trim() !== '';
    const hasFilters = filterBarangay || filterType || filterStatus;
    
    if (!hasSearchTerm && !hasFilters) {
      setCustomerResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleCustomerSearch();
    }, 500);

    return () => clearTimeout(timer);
  }, [customerSearch, filterBarangay, filterType, filterStatus]);

  const loadBills = async (page = 1) => {
    setLoading(true);
    const result = await billingService.getBills(page, itemsPerPage, 'dateencoded', false);
    if (result.success) {
      setBills(result.data);
      setTotalCount(result.count);
      setCurrentPage(page);
      setCurrentRecentBillsPage(1);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const loadCustomerTypes = async () => {
    const result = await billingService.getCustomerTypes();
    if (result.success) {
      setCustomerTypes(result.data);
    }
  };

  const loadSurchargeSettings = async () => {
    const result = await paymentService.getSurchargeSettings();
    if (result.success) {
      setSurchargeSettings(result.data);
    }
  };

  const loadBarangays = async () => {
    const result = await paymentService.getBarangays();
    if (result.success) {
      setBarangays(result.data);
    }
  };

  const handleCustomerSearch = async () => {
    setSearchingCustomers(true);
    setError('');
    
    const result = await paymentService.searchCustomers(customerSearch || '', {
      barangay: filterBarangay,
      type: filterType,
      status: filterStatus
    });
    
    if (result.success) {
      setCustomerResults(result.data);
      if (result.data.length === 0) {
        // setError('No customers found matching your criteria'); // Optional: show as alert instead
      }
    } else {
      setError(result.error);
    }
    setSearchingCustomers(false);
  };

  const handleClearFilters = () => {
    setCustomerSearch('');
    setFilterBarangay('');
    setFilterType('Residential & Commercial');
    setFilterStatus('Active');
    setCustomerResults([]);
  };

  const handleSelectCustomerForBilling = (customer) => {
    setPreSelectedCustomer(customer);
    openAddModal();
  };

  const openAddModal = () => {
    setModalMode('add');
    setSelectedBill(null);
    setShowModal(true);
  };

  const handleEdit = (bill) => {
    setModalMode('edit');
    setSelectedBill(bill);
    setShowModal(true);
  };

  const handleDeleteClick = (bill) => {
    setBillToDelete(bill);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!billToDelete) return;
    
    setLoading(true);
    const result = await billingService.deleteBill(billToDelete.billid);
    if (result.success) {
      setSuccess('Bill deleted successfully');
      loadBills(currentPage);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
    setShowDeleteConfirm(false);
    setBillToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBillToDelete(null);
  };

  const handleModalSuccess = (message) => {
    setSuccess(message);
    loadBills(currentPage);
    setPreSelectedCustomer(null);
    
    const hasFilters = filterBarangay || filterType || filterStatus || customerSearch.trim() !== '';
    if (hasFilters) {
      handleCustomerSearch();
    }
  };

  const handleExportCSV = () => {
    const csv = billingService.exportToCSV(bills);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bills_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long'
    });
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <SystemThemeProvider systemId="water-billing">
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Banner */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ color: '#059669' }} /> Billing & Meter Reading
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Encode monthly meter readings, calculate consumption charges, and monitor billing records.
            </Typography>
          </Box>
          {bills.length > 0 && activeTab === 0 && (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, '&:hover': { bgcolor: '#ffffff', borderColor: '#cbd5e1' } }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2.5 }}>
            {success}
          </Alert>
        )}

        {/* Modern Tabs */}
        <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#059669',
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#059669'
                }
              }
            }}
          >
            <Tab icon={<DescriptionIcon />} iconPosition="start" label="Bill Management & Search" />
            <Tab icon={<BarChartIcon />} iconPosition="start" label="Encoding Analytics & Metrics" />
          </Tabs>
        </Box>

        {/* Tab Content: Bill Management */}
        {activeTab === 0 && (
          <Stack spacing={3}>
            {/* Customer Search Section */}
            <Card sx={{ p: 2.5, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon sx={{ color: '#059669', fontSize: 20 }} /> Encode New Bill — Search Consumer Account
              </Typography>
              
              <Grid container spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search by customer name or account ID..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCustomerSearch()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#94a3b8' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button 
                            variant="contained" 
                            size="small"
                            onClick={handleCustomerSearch}
                            disabled={searchingCustomers}
                            sx={{ borderRadius: 2, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                          >
                            Search
                          </Button>
                        </InputAdornment>
                      ),
                      sx: { borderRadius: 2.5 }
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Barangay</InputLabel>
                    <Select
                      value={filterBarangay}
                      label="Barangay"
                      onChange={(e) => setFilterBarangay(e.target.value)}
                      sx={{ borderRadius: 2.5 }}
                    >
                      <MenuItem value="">All Barangays</MenuItem>
                      {barangays.map((b) => (
                        <MenuItem key={b.barangay} value={b.barangay}>{b.barangay}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      label="Type"
                      onChange={(e) => setFilterType(e.target.value)}
                      sx={{ borderRadius: 2.5 }}
                    >
                      <MenuItem value="Residential & Commercial">Residential & Commercial</MenuItem>
                      <MenuItem value="">All Types</MenuItem>
                      {customerTypes.map((ct) => (
                        <MenuItem key={ct.type} value={ct.type}>{ct.type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      sx={{ borderRadius: 2.5 }}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="Disconnected">Disconnected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    disabled={!customerSearch && !filterBarangay && !filterType && !filterStatus}
                    sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#64748b' }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>

              {/* Customer Search Results */}
              {customerResults.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                      Consumers in {filterBarangay ? `Barangay ${filterBarangay}` : 'Search Results'} ({customerResults.length} total)
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8faf9' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Account ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Account Holder</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Address</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Meter No.</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Barangay</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#475569' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerResults.map((customer) => (
                          <TableRow 
                            key={customer.customerid} 
                            hover 
                            onClick={() => handleSelectCustomerForBilling(customer)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8faf9' } }}
                          >
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#0f172a' }}>{customer.customerid}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{customer.name}</TableCell>
                            <TableCell sx={{ color: '#475569' }}>{customer.address || '—'}</TableCell>
                            <TableCell sx={{ color: '#475569' }}>{customer.meterno || '—'}</TableCell>
                            <TableCell><Chip label={customer.type} size="small" sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600, fontSize: '0.72rem' }} /></TableCell>
                            <TableCell sx={{ color: '#475569' }}>{customer.barangay}</TableCell>
                            <TableCell>
                              <Chip 
                                label={customer.status} 
                                size="small" 
                                sx={{ 
                                  bgcolor: customer.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                                  color: customer.status === 'Active' ? '#047857' : '#b91c1c',
                                  fontWeight: 700,
                                  fontSize: '0.72rem'
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCustomerForBilling(customer);
                                }}
                                sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}
                              >
                                Encode Bill
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Card>

            {/* Recent Bills Section (Hidden when a Barangay is selected) */}
            {!filterBarangay && (
              <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: '#059669', fontSize: 20 }} /> Complete Water Bills Archive
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total: {totalCount} Records
                </Typography>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                  <CircularProgress sx={{ color: '#059669' }} size={32} />
                  <Typography sx={{ ml: 2, color: '#64748b', fontWeight: 500 }}>Loading bills...</Typography>
                </Box>
              ) : bills.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 6, bgcolor: '#ffffff' }}>
                  <Typography color="#64748b" fontWeight={500}>No bills found matching your records</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Bill / OR ID</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Consumer Name</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Barangay</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Billed Month</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Consumption</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Encoded By</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8125rem' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bills.map((bill) => (
                          <TableRow 
                            key={bill.billid} 
                            hover
                            onClick={() => handleViewBillDetails(bill)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#f8faf9' }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#0f172a' }}>{bill.billid}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} color="#0f172a">{bill.customers?.name || 'N/A'}</Typography>
                              <Typography variant="caption" color="text.secondary">ID: {bill.customerid}</Typography>
                            </TableCell>
                            <TableCell sx={{ color: '#475569' }}>{bill.customers?.barangay || 'N/A'}</TableCell>
                            <TableCell sx={{ color: '#475569' }}>{formatMonth(bill.billedmonth)}</TableCell>
                            <TableCell align="right" sx={{ color: '#475569', fontWeight: 600 }}>{bill.consumption} m³</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a' }}>{formatCurrency(bill.basicamount)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={bill.paymentstatus} 
                                size="small" 
                                sx={{
                                  bgcolor: bill.paymentstatus === 'Paid' ? '#ecfdf5' : bill.paymentstatus === 'Partial' ? '#fef3c7' : '#fee2e2',
                                  color: bill.paymentstatus === 'Paid' ? '#047857' : bill.paymentstatus === 'Partial' ? '#92400e' : '#b91c1c',
                                  fontWeight: 700,
                                  fontSize: '0.72rem'
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#64748b', fontSize: '0.8125rem' }}>{bill.encodedby || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={0.5} justifyContent="center">
                                <Tooltip title="Edit Bill">
                                  <IconButton 
                                    size="small" 
                                    sx={{ color: '#0284c7' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(bill);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Bill">
                                  <IconButton 
                                    size="small" 
                                    sx={{ color: '#dc2626' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(bill);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2.5, borderTop: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                      <Pagination 
                        count={totalPages} 
                        page={currentPage} 
                        onChange={(e, p) => loadBills(p)}
                        sx={{
                          '& .Mui-selected': {
                            bgcolor: '#059669 !important',
                            color: '#ffffff'
                          }
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Card>
            )}
          </Stack>
        )}

        {/* Tab Content: Encoding Stats */}
        {activeTab === 1 && (
          <Box>
            <EncodingStats onCustomerClick={handleSelectCustomerForBilling} />
          </Box>
        )}

        {/* Bill Modal */}
        <BillModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setPreSelectedCustomer(null);
          }}
          mode={modalMode}
          bill={selectedBill}
          preSelectedCustomer={preSelectedCustomer}
          onSuccess={handleModalSuccess}
          customerTypes={customerTypes}
          surchargeSettings={surchargeSettings}
        />
        
        {/* Bill Details Modal */}
        <BillDetailsModal 
          isOpen={showBillDetails}
          onClose={() => setShowBillDetails(false)}
          bill={selectedBillDetails?.bill}
          customer={selectedBillDetails?.customer}
          history={selectedBillDetails?.history}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteConfirm && billToDelete !== null}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Confirm Bill Deletion"
          message={billToDelete ? `Are you sure you want to delete bill #${billToDelete.billid}?` : ''}
          subMessage={billToDelete ? `Customer: ${billToDelete.customers?.name || 'N/A'} | Month: ${new Date(billToDelete.billedmonth).toLocaleDateString('en-PH', { year: 'numeric', month: 'long' })}` : ''}
          confirmText="Delete Bill"
          cancelText="Cancel"
          type="danger"
          loading={loading}
        />
      </Box>
    </SystemThemeProvider>
  );
};

export default BillingPage;
